import { useMemo, useState, useCallback, FC, useRef, MouseEvent } from 'react';
import styled from 'styled-components';
import { Popover, CaptionBold, LabelBold, LabelRegular, Small } from '@comeon/ui';
import { QuickDepositLink } from './right-container-header-buttons';
import Icon, {
	ICON_BOTTOM,
	ICON_INFO,
	ICON_RIGHT,
	ICON_QUICK_DEPOSIT_COINS,
	ICON_QUICK_DEPOSIT_LIGHTNING_NEW
} from '~/components/icon';
import { BalanceDisplay, BalanceIconWrapper, BalanceText, BalanceTextLabelWrapper } from '~/components/balance';
import BinaryQuestion from '~/components/binary-question';
import { useSelector } from 'react-redux';
import { motion } from 'motion/react';
import { getZindexByType } from '~/utils/style-utils';
import { showWelcomePopupSelector } from '~/components/authentication/ducks/selectors';
import useGlobalQuickDeposit from '../../hooks/use-global-quick-deposit';
import { BiggerContainer, SmallerContainer } from '../layout/mediaquery';

interface BalanceData {
	label: string;
	value: number;
	key: string;
}

interface DetailedBalanceDropDownProps {
	translations: {
		AVAILABLE_BALANCE_CASINO: string;
		AVAILABLE_BALANCE_SPORTSBOOK: string;
		TOTAL_BALANCE: string;
		MOBILE_HEADER_BAR_DEPOSIT: string;
		MORE_INFO_BALANCE_CTA: string;
		MORE_INFO_BALANCE_POPUP_TITLE: string;
		MORE_INFO_BALANCE_POPUP_CONFIRM: string;
		MORE_INFO_BALANCE_POPUP_DESC: string;
		MORE_INFO_BALANCE_POPUP_DESC_2: string;
	};
	balances: {
		casinoBalance: number;
		sportsbookBalance: number;
		totalBalance: number;
	};
}

interface IDepositIconHeader {
	isQuickDepositAvailable: boolean;
}

const variantsRotate = {
	down: { rotate: '0deg', y: ['0px', '-2px', '2px', '0px'] },
	up: { rotate: '180deg', y: ['0px', '2px', '-2px', '0px'] }
};

const MotionIconWrapper = styled(motion.span)`
	width: 24px;
	height: 24px;
	margin-left: auto;
	> svg {
		height: 24px;
		width: 24px;
		fill: var(--header-balance-dropdown-icon-color, currentcolor);
	}
`;
const StyledPopoverContent = styled(Popover.Content)`
	z-index: ${getZindexByType('modal-view')};
	border-radius: 12px;
	background-color: var(--header-balance-popover-background);
	width: 200px;
`;

const StyledDepositLink = styled(QuickDepositLink)`
	padding: 18px 6px 16px 12px;
	display: flex;
	align-items: center;
	flex: 1;
	width: 100%;
`;
const StyledQuickDepositLinkIcon = styled(QuickDepositLink)`
	margin-right: 6px;
	display: flex;
	z-index: 1;
	position: relative;

	.icon--sprite {
		width: 24px;
		height: 24px;
	}
`;
const StyledDepositIcon = styled(BalanceIconWrapper)`
	height: 24px;
	width: 24px;
	margin-left: 0;
	margin-right: 6px;
`;

const StyledCaption = styled(CaptionBold)`
	color: var(--header-balance-deposit-text);
`;

const BalanceTable = styled.div`
	display: flex;
	flex-direction: column;
	overflow: hidden;
`;
const BalanceRow = styled.div<{ clickable?: boolean }>`
	display: flex;
	flex-direction: row;
	align-items: center;
	padding: 8px 12px 6px 12px;
	border-top: 2px solid var(--account-menu-balance-table-border);
	cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
`;
const BalanceCell = styled.div<{ rightColumn?: boolean }>`
	flex: 1;
	${({ rightColumn }) =>
		rightColumn &&
		`
			flex: none;
      text-align: right;
    `}
`;
const MorInfoText = styled(CaptionBold)`
	color: var(--account-menu-balance-more-info-text);
`;
const IconContainer = styled.span`
	float: right;
	color: var(--header-balance-deposit-icon);
`;
const StyledIconRight = styled.span`
	float: right;
	margin-left: auto;
	color: var(--header-balance-deposit-icon);
`;
const handleIconPropagation = (e: Event) => {
	e.stopPropagation();
};
const DepositIcon: FC<IDepositIconHeader> = ({ isQuickDepositAvailable }) => {
	if (isQuickDepositAvailable) {
		return (
			<StyledQuickDepositLinkIcon onClick={handleIconPropagation}>
				<StyledDepositIcon primary iconprimary small>
					<Icon type={ICON_QUICK_DEPOSIT_LIGHTNING_NEW as string} />
				</StyledDepositIcon>
			</StyledQuickDepositLinkIcon>
		);
	}

	return (
		<StyledDepositIcon primary iconprimary small>
			<Icon type={ICON_QUICK_DEPOSIT_COINS} />
		</StyledDepositIcon>
	);
};

function isTargetClicked(event: MouseEvent<HTMLButtonElement>, dataAt: string) {
	const targetElement = event.target as HTMLElement;
	if (!event || !targetElement) {
		return false;
	}
	if (targetElement.matches(`[data-at="${dataAt}"]`) || targetElement.closest(`[data-at="${dataAt}"]`)) {
		return true;
	}
	return false;
}

const DetailedBalanceDropDown: FC<DetailedBalanceDropDownProps> = ({ translations, balances }) => {
	const [showPopver, setShowPopover] = useState<boolean>(false);
	const [showMoreInfo, setShowMoreInfo] = useState<boolean>(false);
	const shouldShowWelcomePopup = useSelector(showWelcomePopupSelector);
	const { isQuickDepositAvailable } = useGlobalQuickDeposit(false);
	const depositButtonRef = useRef(null);
	const iconType = isQuickDepositAvailable ? (ICON_QUICK_DEPOSIT_LIGHTNING_NEW as string) : ICON_QUICK_DEPOSIT_COINS;

	const togglePopup = useCallback(() => {
		setShowMoreInfo(!showMoreInfo);
	}, [showMoreInfo]);

	const togglePopover = useCallback((e: MouseEvent) => {
		if (isTargetClicked(e as MouseEvent<HTMLButtonElement>, 'deposit-icon-header')) {
			return;
		}
		setShowPopover(popover => !popover);
	}, []);
	const togglePopoverPopup = useCallback(() => {
		setShowPopover(popover => !popover);
	}, []);

	const data: BalanceData[] = useMemo(
		() => [
			{
				label: translations.AVAILABLE_BALANCE_CASINO,
				value: balances.casinoBalance,
				key: 'casino'
			},
			{
				label: translations.AVAILABLE_BALANCE_SPORTSBOOK,
				value: balances.sportsbookBalance,
				key: 'sportsbook'
			},
			{
				label: translations.TOTAL_BALANCE,
				value: balances.totalBalance,
				key: 'total-balance'
			}
		],
		[balances, translations]
	);
	return (
		<>
			<Popover.Root placement="bottom-end" onOpenChange={togglePopoverPopup} isOpen={showPopver}>
				<Popover.Trigger onClick={togglePopover} disabled={shouldShowWelcomePopup}>
					<BalanceDisplay data-at="header-balance-dropdown" labelFirst>
						<QuickDepositLink ref={depositButtonRef}>
							<SmallerContainer>
								<DepositIcon isQuickDepositAvailable={isQuickDepositAvailable} />
							</SmallerContainer>
							<BiggerContainer>
								<BalanceTextLabelWrapper labelFirst>
									{translations.MOBILE_HEADER_BAR_DEPOSIT}
								</BalanceTextLabelWrapper>
							</BiggerContainer>
						</QuickDepositLink>
						<BalanceText>{balances.totalBalance || 0}</BalanceText>
						<MotionIconWrapper
							initial="down"
							variants={variantsRotate}
							animate={showPopver ? 'up' : 'down'}
							transition={{
								x: { type: 'spring', stiffness: 100, damping: 100 }
							}}
						>
							<Icon type={ICON_BOTTOM} />
						</MotionIconWrapper>
					</BalanceDisplay>
				</Popover.Trigger>
				<Popover.Portal>
					<Popover.Overlay lockScroll />
					<StyledPopoverContent>
						<StyledDepositLink onClick={togglePopover}>
							<StyledDepositIcon primary iconprimary>
								<Icon type={iconType} />
							</StyledDepositIcon>
							<StyledCaption>{translations.MOBILE_HEADER_BAR_DEPOSIT}</StyledCaption>
							<StyledIconRight>
								<Icon type={ICON_RIGHT} />
							</StyledIconRight>
						</StyledDepositLink>
						<BalanceTable>
							{data.map(product => (
								<BalanceRow key={product.key} clickable={false}>
									<BalanceCell>
										<Small>{product.label}</Small>
									</BalanceCell>
									<BalanceCell rightColumn data-at={`${product.key}-balance-header`}>
										<LabelBold>{product.value}</LabelBold>
									</BalanceCell>
								</BalanceRow>
							))}
							<BalanceRow onClick={togglePopup} clickable>
								<BalanceCell>
									<MorInfoText>{translations.MORE_INFO_BALANCE_CTA}</MorInfoText>
								</BalanceCell>
								<BalanceCell rightColumn>
									<IconContainer>
										<Icon type={ICON_INFO} />
									</IconContainer>
								</BalanceCell>
							</BalanceRow>
						</BalanceTable>
					</StyledPopoverContent>
				</Popover.Portal>
			</Popover.Root>
			{showMoreInfo && (
				<BinaryQuestion
					shouldOpenBinary
					title={translations.MORE_INFO_BALANCE_POPUP_TITLE}
					yesText={translations.MORE_INFO_BALANCE_POPUP_CONFIRM}
					onYesClick={togglePopup}
				>
					<LabelRegular>{translations.MORE_INFO_BALANCE_POPUP_DESC}</LabelRegular>
					<LabelRegular>{translations.MORE_INFO_BALANCE_POPUP_DESC_2}</LabelRegular>
				</BinaryQuestion>
			)}
		</>
	);
};

export default DetailedBalanceDropDown;
